"use client";

import { CheckCircleIcon } from "@phosphor-icons/react";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3, ListChecks } from "lucide-react";

import { Badge } from "@/src/components/shadcn/ui/badge";
import { ValerieCard } from "@/src/components/valerie-card";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";

import type { ReactNode } from "react";

export type ReferralWorkflowStatus =
  | "automated"
  | "verified"
  | "verify"
  | "needs_action"
  | "failed"
  | "waiting"
  | "not_run"
  | "not_needed";

/** Temporary Confluent copy of the current TPR presentation, pending the shared TPR refactor. */
export type ReferralWorkflowStep = {
  title: string;
  status: ReferralWorkflowStatus;
  summary: string;
  content?: ReactNode;
  continuation?: {
    instruction: string;
    control: ReactNode;
  };
};

export function stepStatus(
  step: TaskStep | undefined,
  confirmed: boolean,
  needsAction = false,
  skipped = false
): ReferralWorkflowStatus {
  if (!step) return "not_run";
  if (step.status === TaskStepStatus.FAILED) return "failed";
  if (needsAction) return "needs_action";
  // Referral steps only pause on automatic timed holds, so a paused step is waiting rather than blocked.
  if (step.status === TaskStepStatus.ASYNC || step.status === TaskStepStatus.PAUSED) return "waiting";
  // Early exits are also persisted as completed. Require success evidence before showing the checkmark.
  if (step.status === TaskStepStatus.COMPLETED && confirmed) return skipped ? "not_needed" : "automated";

  return "needs_action";
}

export function outputId(step: TaskStep | undefined, key: string): string | null {
  const value = step?.output?.[key];

  return typeof value === "string" && value.trim() ? value : null;
}

export function recordOrigin(created: unknown): string {
  return created === true ? "New" : created === false ? "Existing" : "Not recorded";
}

export type DecisionMetadataItem = { label: string; value: ReactNode };

export function ReferralWorkflowLayout({ children }: { children: ReactNode }) {
  return <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto">{children}</div>;
}

export function DecisionMetadata({ items }: { items: DecisionMetadataItem[] }) {
  return (
    <dl className="flex flex-wrap gap-2">
      {items.map((item) => (
        <div
          key={item.label}
          className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"
        >
          <dt className="text-muted-foreground">{item.label}</dt>
          <dd className="font-medium text-foreground">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

const STATUS_PRESENTATION = {
  automated: {
    label: "Automated",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />,
  },
  verified: {
    label: "Verified",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />,
  },
  verify: {
    label: "Verify",
    className: "border-warn/30 bg-warn/10 text-warn-foreground",
    icon: <CircleAlert />,
  },
  needs_action: {
    label: "Needs action",
    className: "border-warn-foreground/30 bg-warn-foreground/10 text-warn-foreground",
    icon: <CircleAlert />,
  },
  failed: {
    label: "Failed",
    className: "border-destructive-foreground/30 bg-destructive-foreground/10 text-destructive-foreground",
    icon: <CircleAlert />,
  },
  waiting: {
    label: "Waiting",
    className: "border-border bg-muted text-muted-foreground",
    icon: <Clock3 />,
  },
  not_run: {
    label: "Not run",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />,
  },
  not_needed: {
    label: "Not needed",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />,
  },
} satisfies Record<ReferralWorkflowStatus, { label: string; className: string; icon: ReactNode }>;

function ReferralWorkflowStepCard({ step }: { step: ReferralWorkflowStep & { number: number } }) {
  const status = STATUS_PRESENTATION[step.status];
  const isSkipped = step.status === "not_needed";
  const cardVariant =
    step.status === "automated" || step.status === "verified"
      ? "success"
      : step.status === "failed"
        ? "destructive"
        : step.status === "needs_action" || step.status === "verify"
          ? "warn"
          : "outline";
  const body = (
    <>
      {step.content}
      {step.continuation ? (
        <div className="space-y-3 border-t pt-4">
          <p className="text-sm text-muted-foreground">{step.continuation.instruction}</p>
          <div className="flex justify-center">{step.continuation.control}</div>
        </div>
      ) : null}
    </>
  );

  return (
    <ValerieCard
      title={`${step.number}. ${step.title}`}
      icon={step.status === "automated" ? undefined : status.icon}
      className={
        step.status === "not_run" || isSkipped ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined
      }
      variant={cardVariant}
      actions={
        step.status === "automated" ? (
          <span className="text-success-foreground" aria-label="Automated">
            <CheckCircleIcon weight="fill" className="size-5" />
          </span>
        ) : (
          <Badge variant="outline" className={status.className}>
            {status.label}
          </Badge>
        )
      }
      contentClassName="space-y-3"
    >
      {isSkipped ? (
        <details>
          <summary className="cursor-pointer text-sm text-muted-foreground">{step.summary}</summary>
          <div className="mt-3 space-y-3">{body}</div>
        </details>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{step.summary}</p>
          {body}
        </>
      )}
    </ValerieCard>
  );
}

export function ReferralWorkflowTimeline({
  steps,
  isProcessing = false,
  waitingMessage,
  priorityContent,
  summaryContent,
}: {
  steps: ReferralWorkflowStep[];
  isProcessing?: boolean;
  waitingMessage?: string;
  priorityContent?: ReactNode;
  summaryContent?: ReactNode;
}) {
  const numberedSteps = steps.map((step, index) => ({ ...step, number: index + 1 }));
  const actions = numberedSteps.filter((step) => ["needs_action", "failed", "verify"].includes(step.status));
  const hasFailure = steps.some((step) => step.status === "failed");
  const summaryVariant = hasFailure
    ? "destructive"
    : actions.length || waitingMessage
      ? "warn"
      : isProcessing
        ? "outline"
        : "success";

  return (
    <div className="space-y-8 pb-8">
      <ValerieCard
        title="Actions remaining"
        icon={<ListChecks />}
        variant={summaryVariant}
        contentClassName="space-y-4"
      >
        {priorityContent}
        {waitingMessage ? (
          <p className="text-sm">{waitingMessage}</p>
        ) : actions.length ? (
          <ol className="space-y-2 text-sm">
            {actions.map((step) => (
              <li key={step.number}>
                <strong>
                  Step {step.number}: {step.title}
                </strong>
                <span className="block text-muted-foreground">{step.summary}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm">
            {isProcessing
              ? "Automation is still running. This timeline will update as each durable workflow step completes."
              : "No manual actions remain."}
          </p>
        )}
        {summaryContent}
      </ValerieCard>
      {numberedSteps.map((step) => (
        <ReferralWorkflowStepCard key={step.number} step={step} />
      ))}
    </div>
  );
}
